import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cx } from "../lib/format";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Footer slot — typically a Button row, right-aligned. */
  footer?: ReactNode;
  /** Max width utility class; default max-w-lg. */
  width?: string;
  children: ReactNode;
}

export function Modal({ open, onClose, title, footer, width = "max-w-lg", children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape closes; focus moves into the panel on open.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div aria-hidden="true" className="absolute inset-0 bg-ink/80" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cx(
          "relative z-10 w-full rounded border border-line bg-surface-raised outline-none",
          width,
        )}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h3 className="font-display text-base tracking-display text-bright">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-muted transition-colors hover:text-bright"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
